import { useState, useEffect, useCallback, useRef } from 'react';
import { authFetch } from '../../../../utils/authFetch';
import { TimelineAPI } from '../../../../connections/Timeline';
import usePolling from '../../../../hooks/usePolling';
import { POLLING_INTERVALS } from '../../../../config/polling';
import { logError, logWarning, getUserFriendlyErrorMessage } from '../../../../utils/errorLogging';
import { mapIncidentDetails } from './mapIncidentDetails';

export const useIncidentDetails = (incidentNumber, enabled = false, enablePolling = true, fetchTimeline = true) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Use ref to track data state without causing re-renders
  const dataRef = useRef(data);

  // Sync ref with data state
  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  const fetchDetails = useCallback(async (isBackgroundRefresh = false, signal = null) => {
    // Use ref to capture current data state without dependency
    const hasCurrentData = !!dataRef.current;

    try {
      // Only show full loading spinner on initial load
      if (isBackgroundRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      // Fetch problem details
      const response = await authFetch(`/api/v1/problem/${encodeURIComponent(incidentNumber)}?mode=viewer`, { signal });

      // Defense in depth: native fetch throws AbortError on aborted requests,
      // but a response that arrived in the same tick the abort fired can still
      // resolve normally. Check the signal after each await to discard the
      // stale payload before it overwrites state for the new incident.
      if (signal?.aborted) return;

      if (response.status === 404) {
        throw new Error('Incident not found');
      }

      if (!response.ok) {
        throw new Error(`Failed to fetch incident details: ${response.status}`);
      }

      const response_data = await response.json();
      if (signal?.aborted) return;
      const problemData = response_data.problem; // Data is nested under 'problem' key

      if (!problemData) {
        throw new Error('No problem data in response');
      }

      // Extract first outage from outages object
      const outageKeys = Object.keys(problemData.outages || {});
      const firstOutageKey = outageKeys[0];
      const outageData = problemData.outages[firstOutageKey] || {};

      // Get timeline from outage notes (for backward compatibility with fallback)
      const timeline = outageData.notes || [];

      // Fetch consolidated timeline (notes + communications + workflows)
      let timelineEvents = [];

      // Callers that never render the consolidated timeline (e.g. the past-incident
      // inline preview) opt out via fetchTimeline=false. Skipping the block avoids
      // paging a timeline the UI discards; timelineEvents stays [] and is threaded
      // to mapIncidentDetails unchanged. The problem-details fetch above still ran.
      if (fetchTimeline) {
        try {
          // Initial load AND background refresh both fetch the COMPLETE timeline
          // by paging, so long incidents never silently drop their oldest entries
          // and background polls stay consistent with the initial load. The
          // safety-cap / partial-page safeguard lives inside
          // getAllConsolidatedTimeline (logWarning → New Relic AppWarning).
          timelineEvents = await TimelineAPI.getAllConsolidatedTimeline(
            incidentNumber,
            firstOutageKey,
            signal,
          );
          if (signal?.aborted) return;
        } catch (timelineError) {
          // AbortError propagates to the outer catch, which discards it cleanly.
          if (timelineError.name === 'AbortError') {
            throw timelineError;
          }

          // If consolidated timeline fails, log error but continue with basic data
          logWarning('useIncidentDetails.fetchDetails', 'Failed to fetch consolidated timeline, falling back to notes only', {
            incidentNumber: incidentNumber,
            error: timelineError.message
          });

          // Fallback: transform notes into basic timeline events
          timelineEvents = (timeline || []).map((note, index) => ({
            id: note.id || `note-${index}`,
            eventType: 'note',
            timestamp: note.created || note.createdAt || note.timestamp,
            author: {
              name: typeof note.author === 'object' && note.author.name
                ? note.author.name
                : typeof note.author === 'string'
                  ? note.author
                  : 'Unknown'
            },
            data: note
          }));
        }
      }

      // Final abort check before writing state. The timeline-catch fallback
      // path above bypasses the in-flight signal check, so a stale fetch that
      // raced through TimelineAPI.error → fallback could otherwise overwrite
      // the new incident's data here.
      if (signal?.aborted) return;

      // All field mapping lives in mapIncidentDetails — add new fields there.
      setData(mapIncidentDetails({
        problemData,
        outageData,
        validatedNumber: incidentNumber,
        firstOutageKey,
        timelineEvents,
      }));

      setLastUpdated(new Date());
      setError(null);
      setIsInitialLoad(false);
    } catch (err) {
      // Ignore aborted requests (user navigated away or incident changed)
      if (err.name === 'AbortError') {
        return;
      }

      // Use secure logging utility
      logError('useIncidentDetails.fetchDetails', err, {
        incidentNumber,
        isBackgroundRefresh,
        hasData: hasCurrentData
      });

      // On background refresh error, keep old data but re-throw so usePolling
      // can apply exponential backoff (see LORE.md).
      if (isBackgroundRefresh) {
        logWarning('useIncidentDetails.fetchDetails', 'Background refresh failed, keeping existing data', {
          incidentNumber,
          hasData: hasCurrentData
        });
        throw err;
      } else {
        // Preserve the original error for known signals (e.g. "Incident not found")
        // so consuming components can branch on error.message. For all other errors,
        // wrap with a user-friendly message.
        if (err.message === 'Incident not found') {
          const notFoundErr = new Error('Incident not found');
          notFoundErr.code = 'NOT_FOUND';
          setError(notFoundErr);
        } else {
          setError(new Error(getUserFriendlyErrorMessage(err)));
        }
        setData(null);
      }
    } finally {
      // Skip the loading flip if the fetch was aborted — the new incident's
      // fetchDetails is already managing its own loading state, and flipping
      // here would briefly clear loading on the new incident's window.
      if (!signal?.aborted) {
        if (isBackgroundRefresh) {
          setRefreshing(false);
        } else {
          setLoading(false);
        }
      }
    }
  }, [incidentNumber, fetchTimeline]);

  // Initial fetch when enabled
  useEffect(() => {
    // Create AbortController for this request
    const abortController = new AbortController();

    // Reset data when disabled (collapsed)
    if (!enabled) {
      setData(null);
      setError(null);
      setIsInitialLoad(true);
      return;
    }

    // Don't fetch if no incident number
    if (!incidentNumber) return;

    // Clear stale data from previous incident before fetching the new one.
    // Without this, navigating from a resolved incident to an active one
    // leaves the old data in state during the fetch window, causing tracking
    // events to mislabel the new incident as "past".
    setData(null);
    setError(null);

    // Initial fetch with abort signal
    fetchDetails(false, abortController.signal);

    // Cleanup: abort in-flight request when incidentNumber changes
    return () => abortController.abort();
  }, [incidentNumber, enabled, fetchDetails]);

  // Background auto-refresh using reusable polling hook
  usePolling(
    () => fetchDetails(true),
    POLLING_INTERVALS.INCIDENT_DETAILS,
    {
      enabled: enabled && !isInitialLoad && enablePolling,
      pauseWhenHidden: true
    }
  );

  return {
    data,
    loading,
    refreshing,
    error,
    lastUpdated,
    refresh: () => fetchDetails(true)
  };
};
